import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useProject } from "@/context/ProjectContext";
import { useAuth } from "@/context/AuthContext";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import Pagination from "@/components/Pagination";
import { MagnifyingGlass, Plus, CurrencyInr, Calendar, Trash } from "@phosphor-icons/react";
import { toast } from "sonner";

const fmtINR = (n) =>
  new Intl.NumberFormat("en-IN", { style: "currency", currency: "INR", maximumFractionDigits: 0 }).format(n || 0);

const STATUS_META = {
  open: { label: "Open", color: "bg-[#E8F0FE] text-[#1967D2] border-[#AECBFA]" },
  accepted: { label: "Accepted", color: "bg-[#E6F4EA] text-[#137333] border-[#A4D0B3]" },
  partially_fulfilled: { label: "Partial", color: "bg-[#FEF7E0] text-[#B06000] border-[#FCE39E]" },
  rejected: { label: "Rejected", color: "bg-[#FCE8E6] text-[#C5221F] border-[#F3B2AC]" },
};

export default function POList({ poType = "material" }) {
  const { activeProjectId } = useProject();
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [pos, setPOs] = useState([]);
  const [totalValue, setTotalValue] = useState(0);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [page, setPage] = useState(1);
  const [pageSize] = useState(25);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState("all");
  const [category, setCategory] = useState("all");
  const [boqMissing, setBoqMissing] = useState(false);
  const [search, setSearch] = useState("");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [selected, setSelected] = useState(() => new Set());
  const [bulkDeleting, setBulkDeleting] = useState(false);

  const isContract = poType === "contract";
  const newRoute = isContract ? "/contracts/new" : "/pos/new";
  const detailBase = isContract ? "/contracts" : "/pos";

  const load = async (targetPage = page) => {
    setLoading(true);
    try {
      const params = { po_type: poType, page: targetPage, page_size: pageSize };
      if (status !== "all") params.status = status;
      if (category !== "all") params.category = category;
      if (boqMissing) params.boq_missing = true;
      if (search) params.search = search;
      if (fromDate) params.from_date = fromDate;
      if (toDate) params.to_date = toDate;
      const { data } = await api.get("/pos", { params });
      setPOs(data.items || []);
      setTotal(data.total || 0);
      setTotalValue(data.total_value || 0);
      setTotalPages(data.total_pages || 1);
      setPage(data.page || 1);
      setSelected(new Set()); // clear selection on new page/filter
    } finally {
      setLoading(false);
    }
  };

  const toggleOne = (id) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };
  const toggleAllOnPage = () => {
    if (selected.size === pos.length && pos.length > 0) {
      setSelected(new Set());
    } else {
      setSelected(new Set(pos.map((p) => p.id)));
    }
  };
  const bulkDelete = async () => {
    const ids = [...selected];
    if (ids.length === 0) return;
    if (!window.confirm(
      `Delete ${ids.length} ${isContract ? "contract" : "material"} PO${ids.length === 1 ? "" : "s"}? This will also delete all attached invoices (files + records). This is permanent.`
    )) return;
    setBulkDeleting(true);
    try {
      const { data } = await api.delete("/pos/bulk", { data: { ids } });
      const msg = `${data.deleted} PO${data.deleted === 1 ? "" : "s"} deleted` +
        (data.invoices_removed ? ` · ${data.invoices_removed} linked invoice${data.invoices_removed === 1 ? "" : "s"} removed` : "");
      toast.success(msg);
      if (data.failed?.length) {
        toast.warning(`${data.failed.length} could not be deleted`);
      }
      await load(1);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setBulkDeleting(false);
    }
  };

  useEffect(() => {
    (async () => {
      if (isContract) {
        try {
          const { data } = await api.get("/catalog/categories", { params: { type: "contract" } });
          setCategories(data.categories || []);
        } catch (_err) { /* ignore */ }
      } else {
        setCategories([]);
      }
    })();
    setStatus("all");
    setCategory("all");
    setBoqMissing(false);
    setSearch("");
    setPage(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [poType]);

  useEffect(() => {
    load(1);
    setPage(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status, category, boqMissing, poType, activeProjectId, fromDate, toDate]);

  return (
    <div className="p-8 sm:p-10 max-w-[1600px] mx-auto" data-testid="po-list-page">
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 mb-8">
        <div>
          <div className="text-xs uppercase tracking-[0.18em] text-neutral-500 mb-2">{isContract ? "Contracts" : "Ledger"}</div>
          <h1 className="font-display text-4xl font-bold tracking-tight">
            {isContract ? "Contract Purchase Orders" : "Material Purchase Orders"}
          </h1>
          <p className="text-sm text-neutral-500 mt-2">{total} result{total === 1 ? "" : "s"}</p>
        </div>
        <Link to={newRoute}>
          <Button className="rounded-sm h-11 px-5 bg-primary hover:bg-[#0039CC] text-white font-medium gap-2" data-testid="new-po-btn">
            <Plus size={16} weight="bold" /> {isContract ? "New Contract PO" : "New PO"}
          </Button>
        </Link>
      </div>

      {/* Financial summary + date picker */}
      <div className="grid grid-cols-1 lg:grid-cols-[1fr_auto] gap-4 mb-4">
        <div className="bg-white border border-neutral-200 rounded-sm px-6 py-5" data-testid="po-total-value-card">
          <div className="flex items-start justify-between gap-4">
            <div>
              <div className="text-[10px] uppercase tracking-[0.18em] text-neutral-500 mb-1">
                Total PO Value {(fromDate || toDate) ? "(Filtered Period)" : ""}
              </div>
              <div className="font-display text-3xl font-bold tabular-nums text-neutral-900 flex items-center gap-2">
                <CurrencyInr size={26} weight="bold" className="text-primary" />
                {fmtINR(totalValue)}
              </div>
              <div className="text-xs text-neutral-500 mt-1.5">
                Across {total} {isContract ? "contract" : "material"} PO{total === 1 ? "" : "s"}
                {(fromDate || toDate) && (
                  <span className="ml-2 text-primary">
                    · {fromDate || "…"} → {toDate || "…"}
                  </span>
                )}
              </div>
            </div>
          </div>
        </div>
        <div className="bg-white border border-neutral-200 rounded-sm px-4 py-3 flex items-end gap-3">
          <div>
            <label className="text-[10px] uppercase tracking-wider text-neutral-500 mb-1 flex items-center gap-1">
              <Calendar size={11} /> From
            </label>
            <Input
              type="date"
              value={fromDate}
              onChange={(e) => setFromDate(e.target.value)}
              max={toDate || undefined}
              className="rounded-sm h-10 tabular-nums w-[160px]"
              data-testid="po-from-date"
            />
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-wider text-neutral-500 mb-1 flex items-center gap-1">
              <Calendar size={11} /> To
            </label>
            <Input
              type="date"
              value={toDate}
              onChange={(e) => setToDate(e.target.value)}
              min={fromDate || undefined}
              className="rounded-sm h-10 tabular-nums w-[160px]"
              data-testid="po-to-date"
            />
          </div>
          {(fromDate || toDate) && (
            <Button
              variant="ghost"
              className="rounded-sm h-10 text-neutral-500 hover:text-neutral-900"
              onClick={() => { setFromDate(""); setToDate(""); }}
              data-testid="po-clear-dates"
            >
              Clear
            </Button>
          )}
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white border border-neutral-200 rounded-sm p-4 mb-4 flex flex-wrap gap-3 items-end">
        <div className="flex-1 min-w-[220px]">
          <label className="text-[11px] uppercase tracking-wider text-neutral-500 mb-1 block">Search</label>
          <div className="relative">
            <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-neutral-400" />
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && load(1)}
              placeholder="Search PO number or vendor"
              className="rounded-sm pl-9 h-10"
              data-testid="search-input"
            />
          </div>
        </div>
        <div className="w-[180px]">
          <label className="text-[11px] uppercase tracking-wider text-neutral-500 mb-1 block">Status</label>
          <Select value={status} onValueChange={setStatus}>
            <SelectTrigger className="rounded-sm h-10" data-testid="status-filter">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Statuses</SelectItem>
              <SelectItem value="open">Open</SelectItem>
              <SelectItem value="accepted">Accepted</SelectItem>
              <SelectItem value="partially_fulfilled">Partially Fulfilled</SelectItem>
              <SelectItem value="rejected">Rejected</SelectItem>
            </SelectContent>
          </Select>
        </div>
        {isContract ? (
          <div className="w-[200px]">
            <label className="text-[11px] uppercase tracking-wider text-neutral-500 mb-1 block">Contract Type</label>
            <Select value={category} onValueChange={setCategory}>
              <SelectTrigger className="rounded-sm h-10" data-testid="category-filter">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Types</SelectItem>
                {categories.map((c) => (
                  <SelectItem key={c} value={c}>{c}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        ) : (
          <div className="w-[220px]">
            <label className="text-[11px] uppercase tracking-wider text-neutral-500 mb-1 block">BOQ Mapping</label>
            <button
              onClick={() => setBoqMissing((v) => !v)}
              data-testid="filter-boq-missing"
              className={
                "w-full h-10 rounded-sm px-3 text-sm border transition-colors " +
                (boqMissing
                  ? "border-[#B06000] bg-[#FEF7E0] text-[#B06000]"
                  : "border-neutral-300 text-neutral-700 hover:border-primary hover:text-primary")
              }
            >
              {boqMissing ? "Showing unmapped POs only" : "Show unmapped POs (no BOQ)"}
            </button>
          </div>
        )}
        {!isContract && boqMissing && (
          <div className="self-end">
            <Link
              to="/pos/bulk-map"
              className="inline-flex items-center gap-1.5 h-10 px-3 text-sm rounded-sm bg-primary text-white hover:bg-[#0039CC] transition-colors"
              data-testid="link-bulk-map"
            >
              Bulk-map to BOQ →
            </Link>
          </div>
        )}
      </div>

      {/* Table */}
      <div className="bg-white border border-neutral-200 rounded-sm overflow-hidden">
        {isAdmin && selected.size > 0 && (
          <div className="flex items-center justify-between px-6 py-3 bg-[#FCE8E6] border-b border-[#F3B2AC] text-sm text-[#C5221F]" data-testid="pos-bulk-toolbar">
            <span className="font-medium">
              {selected.size} PO{selected.size === 1 ? "" : "s"} selected
            </span>
            <div className="flex items-center gap-2">
              <Button
                variant="ghost"
                size="sm"
                className="rounded-sm h-8 text-[#C5221F] hover:text-[#C5221F]"
                onClick={() => setSelected(new Set())}
                data-testid="pos-bulk-clear"
              >
                Clear
              </Button>
              <Button
                size="sm"
                className="rounded-sm h-8 bg-[#C5221F] hover:bg-[#A80D0B] text-white gap-1.5"
                disabled={bulkDeleting}
                onClick={bulkDelete}
                data-testid="pos-bulk-delete-btn"
              >
                <Trash size={14} weight="bold" />
                {bulkDeleting ? "Deleting…" : `Delete ${selected.size}`}
              </Button>
            </div>
          </div>
        )}
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-neutral-50 text-neutral-600">
              <tr>
                {isAdmin && (
                  <th className="w-10 px-3 py-3 text-center">
                    <Checkbox
                      checked={pos.length > 0 && selected.size === pos.length}
                      onCheckedChange={toggleAllOnPage}
                      aria-label="Select all POs on this page"
                      data-testid="pos-select-all"
                    />
                  </th>
                )}
                <th className="text-left px-6 py-3 font-medium text-xs uppercase tracking-wider">PO #</th>
                <th className="text-left px-4 py-3 font-medium text-xs uppercase tracking-wider">Vendor</th>
                <th className="text-left px-4 py-3 font-medium text-xs uppercase tracking-wider">Category</th>
                <th className="text-left px-4 py-3 font-medium text-xs uppercase tracking-wider">Created</th>
                <th className="text-right px-4 py-3 font-medium text-xs uppercase tracking-wider">Total</th>
                <th className="text-left px-4 py-3 font-medium text-xs uppercase tracking-wider">Status</th>
                <th className="px-4 py-3"></th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={isAdmin ? 8 : 7} className="text-center py-12 text-neutral-400">Loading…</td></tr>
              ) : pos.length === 0 ? (
                <tr><td colSpan={isAdmin ? 8 : 7} className="text-center py-16 text-neutral-400">No purchase orders found</td></tr>
              ) : (
                pos.map((po, idx) => {
                  const meta = STATUS_META[po.status] || STATUS_META.open;
                  const checked = selected.has(po.id);
                  return (
                    <tr key={po.id} className={`border-t border-neutral-100 hover:bg-neutral-50 transition-colors ${idx % 2 ? "bg-neutral-50/40" : ""} ${checked ? "bg-red-50/50" : ""}`} data-testid={`po-row-${po.po_number}`}>
                      {isAdmin && (
                        <td className="w-10 px-3 py-3 text-center" onClick={(e) => e.stopPropagation()}>
                          <Checkbox
                            checked={checked}
                            onCheckedChange={() => toggleOne(po.id)}
                            aria-label={`Select PO ${po.po_number}`}
                            data-testid={`pos-select-${po.po_number}`}
                          />
                        </td>
                      )}
                      <td className="px-6 py-3">
                        <Link to={`${detailBase}/${po.id}`} className="font-mono-num text-primary hover:underline">{po.po_number}</Link>
                      </td>
                      <td className="px-4 py-3 text-neutral-800">{po.vendor_name}</td>
                      <td className="px-4 py-3 text-neutral-600">{po.category}</td>
                      <td className="px-4 py-3 text-neutral-500 tabular-nums">
                        {po.created_at ? new Date(po.created_at).toLocaleDateString() : "—"}
                      </td>
                      <td className="px-4 py-3 text-right tabular-nums font-medium">{fmtINR(po.total_amount)}</td>
                      <td className="px-4 py-3">
                        <span className={`inline-flex items-center gap-1.5 text-[11px] uppercase tracking-wider px-2 py-0.5 border ${meta.color}`}>
                          {meta.label}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-right">
                        <Link to={`${detailBase}/${po.id}`} className="text-primary text-xs uppercase tracking-wider hover:underline">Open →</Link>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
        <Pagination
          page={page}
          pageSize={pageSize}
          total={total}
          totalPages={totalPages}
          onPageChange={(n) => load(n)}
          testIdPrefix="pos"
        />
      </div>
    </div>
  );
}
